import { useEffect } from 'react'
import Dialog from '@mui/material/Dialog'
import { Controller, useForm } from 'react-hook-form'

import Icon from '../../../../components/UI/Icon'
import { RadioButton } from '../../../../components/UI'
import DropDown from '../../../../components/Input/DropDown'

import { useGetInfo, useMainCategories } from '../../../../hooks'

import { upcomingClassesFilterGenders } from '../../../../constants'

const defaultFilters = {
  category: '',
  gender: '_',
  languages: '_',
  country_of_origin: '_',
  country_of_residence: '_',
  search: '',
}

const UpcomingClassesFilter = ({ filters, setFilters, open, onClose }) => {
  const { handleSubmit, setValue, watch, control, register } = useForm({
    defaultValues: defaultFilters,
  })

  const activeCategory = watch('category')

  const mainCategories = useMainCategories()
  const { formattedWorldLanguages, formattedCountries } = useGetInfo()

  const formattedLanguages = [
    {
      value: '_',
      label: 'Any Language',
    },
    ...formattedWorldLanguages,
  ]
  const allFormattedCountries = [
    {
      value: '_',
      label: 'Any Country',
    },
    ...formattedCountries,
  ]

  const handleChangeCategory = (value) => {
    if (value && value === activeCategory) {
      return setValue('category', '')
    }

    setValue('category', value)
  }

  const handleClose = () => {
    onClose()
  }

  const handleRenderDropdownArrow = () => {
    return <Icon name="chevron-down" color="#F67443" className="w-[20px] h-[20px] mr-[16px]" />
  }

  const handleReset = () => {
    setFilters({
      ...filters,
      ...defaultFilters,
    })

    handleClose()
  }

  const handleFormSubmit = (data) => {
    setFilters({
      ...filters,
      ...data,
    })

    handleClose()
  }

  useEffect(() => {
    if (open) {
      setValue('category', filters.category || defaultFilters.category)
      setValue('gender', filters.gender || defaultFilters.gender)
      setValue('languages', filters.languages || defaultFilters.languages)
      setValue('country_of_origin', filters.country_of_origin || defaultFilters.country_of_origin)
      setValue(
        'country_of_residence',
        filters.country_of_residence || defaultFilters.country_of_residence
      )
      setValue('search', filters.search || defaultFilters.search)
    }
  }, [open])

  const upcomingClassesFilterCategoriesContent = mainCategories.map((mainCategory) => {
    return (
      <RadioButton
        key={`category-${mainCategory.slug}`}
        isActive={mainCategory.slug === activeCategory}
        onClick={() => handleChangeCategory(mainCategory.slug)}
      >
        {mainCategory.name}
      </RadioButton>
    )
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      fullWidth
      classes={{
        paper: 'md:!max-w-[709px] !p-[32px] !rounded-[10px] !bg-[#FEEBDD] !shadow-modal',
      }}
    >
      <div className="w-full flex items-center justify-between">
        <h2 className="text-3xl font-semibold text-primary-100 leading-[42px]">Filter</h2>

        <button onClick={handleClose}>
          <Icon name="close" color="#FF9053" className="w-[24px] h-[24px]" />
        </button>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-[24px] w-full">
        <div className="w-full flex flex-col items-start gap-y-[24px]">
          <div className="w-full">
            <h3 className="text-base leading-[24px] text-primary-100 font-semibold mb-[10px]">
              Category
            </h3>

            <div className="w-[90%] flex items-start flex-wrap gap-[10px]">
              {upcomingClassesFilterCategoriesContent}
            </div>
          </div>

          <div className="w-full">
            <h3 className="text-base leading-[24px] text-primary-100 font-semibold mb-[10px]">
              Student’s Gender
            </h3>

            <div className="w-[309px]">
              <Controller
                control={control}
                name="gender"
                render={({ field: { value, onChange } }) => {
                  return (
                    <DropDown
                      menuItems={upcomingClassesFilterGenders}
                      value={value}
                      setValue={onChange}
                      borderColor="!border-[#F67443]"
                      renderIcon={handleRenderDropdownArrow}
                    />
                  )
                }}
              />
            </div>
          </div>

          <div className="w-full">
            <h3 className="text-base leading-[24px] text-primary-100 font-semibold mb-[10px]">
              Student’s Language
            </h3>

            <div className="w-[309px]">
              <Controller
                name="languages"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DropDown
                      menuItems={formattedLanguages}
                      value={value}
                      setValue={onChange}
                      borderColor="!border-[#F67443]"
                      renderIcon={handleRenderDropdownArrow}
                    />
                  )
                }}
              />
            </div>
          </div>

          <div className="w-full">
            <h3 className="text-base leading-[24px] text-primary-100 font-semibold mb-[12px]">
              Where should your student be from?
            </h3>

            <div className="w-full flex items-center gap-[20px]">
              <div className="w-1/2 flex flex-col items-start">
                <span className="text-sm font-medium text-primary-100 mb-[6px]">
                  Student’s Origin Country
                </span>

                <Controller
                  name="country_of_origin"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <DropDown
                        menuItems={allFormattedCountries}
                        value={value}
                        setValue={onChange}
                        borderColor="!border-[#F67443]"
                        renderIcon={handleRenderDropdownArrow}
                      />
                    )
                  }}
                />
              </div>

              <div className="w-1/2 flex flex-col items-start">
                <span className="text-sm font-medium text-primary-100 mb-[6px]">
                  Student’s Residence Country
                </span>

                <Controller
                  name="country_of_residence"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <DropDown
                        menuItems={allFormattedCountries}
                        value={value}
                        setValue={onChange}
                        borderColor="!border-[#F67443]"
                        renderIcon={handleRenderDropdownArrow}
                      />
                    )
                  }}
                />
              </div>
            </div>
          </div>

          <div className="w-full">
            <h3 className="text-base leading-[24px] text-primary-100 font-semibold mb-[6px]">
              Keywords
            </h3>

            <input
              type="text"
              placeholder="Write keywords (ex. tutor’s name, categories, etc.)"
              className="w-full px-[16px] py-[10px] bg-white rounded-[8px] outline-none border-primary-100 border-[1px] placeholder:text-[#F6744399] text-primary-100 text-base font-medium leading-[24px]"
              {...register('search')}
            />
          </div>
        </div>

        <div className="w-full flex items-center justify-end mt-[24px] gap-x-[10px]">
          <button
            type="button"
            className="px-[16px] bg-[#FDFDFD] hover:bg-[#F67443] hover:text-white border-[#F67443] border-[1px] rounded-[8px] shadow-button-second h-[44px] flex items-center justify-center text-base font-semibold text-primary-100 transition-colors duration-200 ease"
            onClick={handleReset}
          >
            Reset
          </button>

          <button
            type="submit"
            className="px-[16px] bg-primary-button hover:bg-primary-button-hover rounded-[8px] shadow-button-second h-[44px] flex items-center justify-center text-base font-semibold text-white transition-colors duration-200 ease"
          >
            Apply Filter
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export default UpcomingClassesFilter
